import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Box,
  Alert,
  AlertIcon,
  Select,
  Spinner,
  Center,
} from '@chakra-ui/react';

import { useColors } from './colors';

const RulesSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  currentSettings,
  storeOptions,
  selectedStoreId,
  onStoreChange,
  isLoading,
}) => {
  const { textColor, inputColor } = useColors();

  const [rules, setRules] = useState({
    minimumOverallMarkup: 0,
    marketplaceCommissionMarkup: 0,
    priceUndercutPercent: 2,
  });

  // Load current settings when modal opens
  useEffect(() => {
    if (currentSettings && isOpen) {
      setRules({
        minimumOverallMarkup: currentSettings.minimumOverallMarkup || 0,
        marketplaceCommissionMarkup: currentSettings.marketplaceCommissionMarkup || 0,
        priceUndercutPercent: currentSettings.priceUndercutPercent || 2,
      });
    }
  }, [currentSettings, isOpen]);

  const handleSave = () => {
    onSave(rules);
    onClose();
  };

  const updateRule = (field, value) => {
    setRules((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <Center p={8}>
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" />
              <Text color={textColor}>Loading store settings...</Text>
            </VStack>
          </Center>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="600px" borderRadius="xl" boxShadow="2xl">
        <ModalHeader color={textColor} fontSize="xl" fontWeight="600" pb={2}>
          Store Pricing Rules
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Store Selection */}
            <FormControl>
              <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                Select Store
              </FormLabel>
              <Select
                placeholder="Choose a store..."
                value={selectedStoreId}
                onChange={(e) => onStoreChange(e.target.value)}
                color={inputColor}
                borderRadius="md"
                borderColor="gray.200"
                _hover={{ borderColor: 'blue.300' }}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              >
                {storeOptions?.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selectedStoreId && (
              <>
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={4}>
                    Pricing Rules
                  </Text>

                  <VStack spacing={6} align="stretch">
                    {/* Minimum Overall Markup */}
                    <FormControl>
                      <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                        Minimum Overall Markup (%)
                      </FormLabel>
                      <NumberInput
                        value={rules.minimumOverallMarkup}
                        onChange={(value) =>
                          updateRule('minimumOverallMarkup', parseFloat(value) || 0)
                        }
                        min={0}
                        max={100}
                        precision={2}
                        step={0.1}
                      >
                        <NumberInputField
                          color={inputColor}
                          borderRadius="md"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px blue.400',
                          }}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Minimum markup percentage to maintain overall profitability
                      </Text>
                    </FormControl>

                    {/* Marketplace Commission Markup */}
                    <FormControl>
                      <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                        Marketplace Commission Markup (%)
                      </FormLabel>
                      <NumberInput
                        value={rules.marketplaceCommissionMarkup}
                        onChange={(value) =>
                          updateRule(
                            'marketplaceCommissionMarkup',
                            parseFloat(value) || 0,
                          )
                        }
                        min={0}
                        max={100}
                        precision={2}
                        step={0.1}
                      >
                        <NumberInputField
                          color={inputColor}
                          borderRadius="md"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px blue.400',
                          }}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Additional markup to cover marketplace commission fees
                      </Text>
                    </FormControl>

                    {/* Price Undercut Percent */}
                    <FormControl>
                      <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                        Price Undercut Percent (%)
                      </FormLabel>
                      <NumberInput
                        value={rules.priceUndercutPercent}
                        onChange={(value) =>
                          updateRule('priceUndercutPercent', parseFloat(value) || 2)
                        }
                        min={0}
                        max={100}
                        precision={1}
                        step={0.5}
                      >
                        <NumberInputField
                          color={inputColor}
                          borderRadius="md"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px blue.400',
                          }}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Percentage below competitor price to maintain competitive
                        advantage
                      </Text>
                    </FormControl>
                  </VStack>
                </Box>

                <Alert
                  status="info"
                  borderRadius="md"
                  bg="blue.50"
                  borderColor="blue.200"
                >
                  <AlertIcon color="blue.500" />
                  <Box>
                    <Text fontSize="sm" color="blue.700">
                      These pricing rules will be applied to all listings in the selected
                      store. The optimization process will ensure prices maintain these
                      minimum margins.
                    </Text>
                  </Box>
                </Alert>
              </>
            )}

            {!selectedStoreId && (
              <Alert
                status="warning"
                borderRadius="md"
                bg="yellow.50"
                borderColor="yellow.200"
              >
                <AlertIcon color="yellow.500" />
                <Box>
                  <Text fontSize="sm" color="yellow.700">
                    Please select a store to configure pricing rules.
                  </Text>
                </Box>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter pt={4} borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" mr={3} onClick={onClose} fontWeight="500">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            borderRadius="md"
            fontWeight="500"
            px={6}
            isDisabled={!selectedStoreId}
          >
            Save Rules
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RulesSettingsModal;
